import { pick } from 'lodash';
import { type ReactElement } from 'react';
import { useEffect, useState } from 'react';
import { type NavigateFunction } from 'react-router-dom';
import { useParams } from 'react-router-dom';

import { Loader } from '@hivebrite/design-system/Loader';

import CurrenciesContext from 'hb-react/frontoffice/contexts/CurrenciesContext';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { displayXhrError } from 'hb-react/frontoffice/utils/globalUtils';
import CombineProvider from 'hb-react/shared/components/CombineProvider';
import LaunchDarklyWrapper from 'hb-react/shared/components/LaunchDarklyWrapper';
import { axiosInstance as axios } from 'hb-react/shared/utils/axios';
import ThemeProvider from 'hb-react/style/ThemeProvider';

import { MODE } from '../constants';
import { JobContext } from '../contexts/JobContext';
import {
  type Currency,
  type Fields,
  type Job,
  type JobContext as JobContextType,
  type JobFormProps,
  type OtherInfos,
} from '../types';

export const Provider = ({
  children,
  navigate,
  themeName = 'fo',
  ...props
}: Partial<JobFormProps> & {
  children: ReactElement;
  navigate?: NavigateFunction;
  themeName?: string;
}) => {
  const { mode } = props;

  const { currentNetwork, currentUser } = useDefaultAppContexts();

  const { opportunityId } = useParams();

  const [currencies, setCurrencies] = useState({} as Array<Currency>);

  const [jobContext, setJobContext] = useState(props as JobContextType);

  const [loadingOtherInfos, setLoadingOtherInfos] = useState<boolean>();
  const [loadingJobData, setLoadingJobData] = useState<boolean>();

  useEffect(() => {
    setLoadingOtherInfos(true);
    axios
      .get(Routes.search_other_infos_path({ format: 'json' }))
      .then(
        ({
          data: {
            currencies: currencyInput,
            job_contract_types: contractTypes,
            job_experience_types: experienceTypes,
            job_kind_configurations: kindConfigurations,
            job_kinds: jobKinds,
          },
        }: {
          data: OtherInfos;
        }) => {
          const fields = kindConfigurations.reduce(
            (result, { fields, kind_id: kindId }) => ({
              ...result,
              [kindId]: fields,
            }),
            {} as { [kindId: number]: Fields },
          );

          const kinds =
            mode === MODE.PO
              ? jobKinds.filter(({ name }) =>
                  [
                    'business_opportunities.kind.full_time_job',
                    'business_opportunities.kind.offer_internship',
                  ].includes(name),
                )
              : jobKinds;

          setCurrencies(currencyInput);

          setJobContext((context) => ({
            ...context,
            contractTypes,
            experienceTypes,
            fields,
            kindConfigurations,
            kinds,
            navigate,
          }));
        },
      )
      .finally(() => {
        setLoadingOtherInfos(false);
      });
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [currentNetwork, currentUser, mode]);

  useEffect(() => {
    if (mode === MODE.FO && currentUser) {
      setLoadingJobData(true);
      const url = opportunityId
        ? Routes.edit_user_business_opportunities_request_path(
            currentUser.id,
            opportunityId,
            { format: 'json' },
          )
        : Routes.new_user_business_opportunities_request_path(currentUser.id, {
            format: 'json',
          });

      axios
        .get(url)
        .then(({ data }: { data: { request: Job } }) =>
          setJobContext((context) => ({
            ...context,
            currentUserId: currentUser?.id,
            customAttributesEnabled:
              currentNetwork.network_configuration?.business_opportunities
                ?.custom_attributes_enabled,
            job: data.request,
            networkId: currentNetwork.id,
            userFieldsConfig: pick(currentNetwork.user_fields_config, [
              'industries_label',
              'industries_placeholder',
            ]),
          })),
        )
        .catch(({ message }) => displayXhrError(message))
        .finally(() => setLoadingJobData(false));
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [currentNetwork, currentUser, mode]);

  return (
    <LaunchDarklyWrapper>
      <CombineProvider
        providers={[
          {
            provider: JobContext.Provider,
            value: jobContext,
          },
          {
            provider: CurrenciesContext.Provider,
            value: currencies,
          },
          {
            provider: ThemeProvider,
            theme: themeName,
          },
        ]}
      >
        {loadingOtherInfos || loadingJobData ? <Loader size={50} /> : children}
      </CombineProvider>
    </LaunchDarklyWrapper>
  );
};
